import { Popover } from '@mui/material';
import React, {
    cloneElement,
    MouseEvent,
    MouseEventHandler,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import useWidth from './components/layout/useWidth';
import { SIZES } from './constants/constants';
import { useCurrentBreakpoint } from './hooks/useCurrentBreakpoint';
import { BaseLayout, BaseLayoutProps, V } from './Layout';
import { getLayoutOverflowConfig } from './utils/layout/layout';
import OverflowMore from './utils/layout/OverflowMore';

type ResponsiveLayoutProps = Omit<BaseLayoutProps, 'ref'> & {
    /** Width of each tile/item in pixels for overflow calculations */
    tileWidth: number;
    /**
     * Callback invoked with the index of the last visible element
     */
    setLastElementToFitIndex?: (index?: number) => void;
    /**
     */
    onViewMore?: () => void;
    /**
     * Custom element or render function for the overflow indicator
     */
    customMoreElement?: ReactElement | ((handleClick?: MouseEventHandler<HTMLButtonElement>) => ReactElement);
    /**
     * Hide the default "more" overflow indicator element
     * @default false
     */
    hideMoreElement?: boolean;
    /**
     * Custom element or render function for the end element
     */
    fixedEndElement?: ReactElement;
};

export default function ResponsiveLayout({
    children,
    tileWidth,
    setLastElementToFitIndex,
    onViewMore,
    customMoreElement,
    layouts,
    hideMoreElement = false,
    hide = false,
    gap = 0,
    fixedEndElement,
    ...rest
}: ResponsiveLayoutProps) {
    const containerRef = useRef<HTMLElement>(null);
    const containerWidth = useWidth(containerRef);
    const bp = useCurrentBreakpoint();

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const content = useMemo<ReactElement[]>(() => {
        if (layouts?.[bp]) return layouts[bp];

        return React.Children.toArray(children) as ReactElement[];
    }, [layouts, bp, children]);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const onOverflowClick: MouseEventHandler = (event) => {
        if (onViewMore) {
            onViewMore();
            return;
        }
        handleClick(event as MouseEvent<HTMLButtonElement>);
    };

    const reservedEndWidth = fixedEndElement ? tileWidth : 0;

    const { fitIndexes, overflowIndexes } = useMemo(() => {
        if (!containerWidth || !tileWidth) return { fitIndexes: content.map((_, i) => i), overflowIndexes: [] };

        const cw = tileWidth + 8 * gap;

        const { visibleCount } = getLayoutOverflowConfig(
            containerWidth,
            cw,
            content,
            !!setLastElementToFitIndex,
            reservedEndWidth
        );

        const visibleIndexes = content.map((_, i) => i).slice(0, visibleCount);
        const overflowIndexes = content.map((_, i) => i).slice(visibleCount);

        return { fitIndexes: visibleIndexes, overflowIndexes };
    }, [content, containerWidth, tileWidth, gap, setLastElementToFitIndex, reservedEndWidth]);

    useEffect(() => {
        setLastElementToFitIndex?.(fitIndexes[fitIndexes.length - 1] ?? 0);
    }, [fitIndexes, setLastElementToFitIndex]);

    const hasMore = overflowIndexes.length > 0;

    return (
        !hide && (
            <>
                <BaseLayout ref={containerRef} layouts={layouts} gap={gap} {...rest}>
                    {fitIndexes.map((i) => cloneElement(content[i], { key: i }))}

                    {hasMore &&
                        (customMoreElement ? (
                            typeof customMoreElement === 'function' ? (
                                customMoreElement(handleClick)
                            ) : (
                                customMoreElement
                            )
                        ) : !hideMoreElement ? (
                            <OverflowMore
                                count={overflowIndexes.length}
                                onClick={onOverflowClick}
                                sx={{ fontSize: SIZES.small }}
                            />
                        ) : null)}
                    {fixedEndElement}
                </BaseLayout>

                {hasMore && (
                    <Popover
                        open={!!anchorEl}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <V sx={{ width: tileWidth, m: 1, gap: 1 }}>
                            {overflowIndexes.map((i) => cloneElement(content[i], { key: i }))}
                        </V>
                    </Popover>
                )}
            </>
        )
    );
}
